import { h, Fragment } from 'preact';
import {
	useRef, useEffect, useCallback, forwardRef, useContext,
} from 'preact/compat';
import type { MutableRef } from 'preact/hooks';
import { Theme } from '../../lib/render';
import { useUser } from '../../hooks/user';
import { useVGS, VGSTypes, VGSErrors } from '../../hooks/vgs';
import Address from './Address';
import FormError from '../inputs/FormError';
import { SubmitJSFlags, SubmitJSState } from '../../types';

/**
 * Simple check if error is present.
 *
 * @param fn Field Name.
 * @param errors Errors object.
 */
const vgsIsErrored = (
	fn: VGSTypes,
	errors: VGSErrors,
): boolean => errors[fn as VGSTypes].error && errors[fn as VGSTypes].touched;

/**
 * Simple credit card error component.
 * Handles own display logic.
 */
const VGSInput = forwardRef((
	{
		fieldName,
		label,
		labelFor,
		inputClass,
	}: {
		fieldName: VGSTypes;
		label: string;
		labelFor: string;
		inputClass: string;
	},
	ref: MutableRef<HTMLDivElement>,
) => {
	const { vgsErrors } = useVGS();
	const error = vgsIsErrored(fieldName, vgsErrors);
	return (
		<div
			className={[
				'ea-vgs-outer',
				error ? 'error' : '',
			].join(' ')}
		>
			<label className="ea-label" htmlFor={labelFor}>{label}</label>
			<div
				className={`${inputClass} ea-vgs-input`}
				ref={ref}
			/>
			{error && (
				<FormError
					error={vgsErrors[fieldName].error as string}
				/>
			)}
		</div>
	);
});

/**
 * Payment Component.
 * Handles:
 * - vgs form render
 * - vgs field listener
 */
const Payment = () => {
	const theme = useContext(Theme);
	const { dispatch } = useUser();
	const { dispatchVgs, vgsInit } = useVGS();

	const ccRef = useRef<HTMLDivElement>(null);
	const expRef = useRef<HTMLDivElement>(null);
	const cvvRef = useRef<HTMLDivElement>(null);

	/**
	 * VGS Error Listener.
	 * Push any errors into state.
	 */
	const vgsListener = useCallback((
		newState: SubmitJSState,
		prevState: SubmitJSState,
		flags: SubmitJSFlags,
	) => {
		if (typeof newState === 'undefined') {
			return;
		}

		/**
		 * Set to touch if !empty.
		 * Or if change triggered.
		 */
		if (flags.hasChanged || newState.isFocused === true) {
			dispatchVgs({
				type: 'TOUCH',
				payload: {
					name: newState.name,
					touched: true,
				},
			});
		}

		/**
		 * Set/remove error.
		 */
		if (!newState.isValid) {
			dispatchVgs({
				type: 'ERROR',
				payload: {
					name: newState.name,
					error: newState.errorMessages[0],
				},
			});
		} else {
			dispatchVgs({
				type: 'ERROR',
				payload: {
					name: newState.name,
					error: false,
				},
			});
		}

		/**
		 * Set user data values.
		 */
		if (newState.name === 'Account' && newState.isValid) {
			dispatch({
				type: 'UPDATE_USER',
				payload: {
					last4: newState.last4,
					cardType: newState.cardType,
				},
			});
		}
	}, []);

	/**
	 * Init the VGS payment form.
	 * Track ref to prevent all rerenders.
	 */
	const vgsInitialized = useRef(false);
	useEffect(() => {
		if (
			vgsInitialized.current
			|| !vgsInit
			|| !ccRef.current
			|| !expRef.current
		) {
			return;
		}

		/**
		 * Divergent logic for caging forms.
		 * No CVV requested.
		 */
		if (theme.donate_type === 'caging') {
			vgsInit.initVgs().then(() => {
				vgsInit.renderCreditCard(ccRef.current, {}, vgsListener);
				vgsInit.renderExpirationDate(expRef.current, {}, vgsListener);
			});
		} else {
			if (
				!cvvRef.current
			) {
				return;
			}
			vgsInit.initVgs().then(() => {
				vgsInit.renderCreditCard(ccRef.current, {}, vgsListener);
				vgsInit.renderExpirationDate(expRef.current, {}, vgsListener);
				vgsInit.renderCvv(cvvRef.current, {}, vgsListener);
			});
		}
		vgsInitialized.current = true;
	}, [vgsInit, vgsListener, ccRef.current, expRef.current, cvvRef.current, vgsInitialized]);

	return (
		<Fragment>
			<div className="ea-field-group">
				<VGSInput
					fieldName={VGSTypes.Account}
					ref={ccRef}
					labelFor="cc-container"
					label="Card number"
					inputClass="cc-input"
				/>
			</div>
			<div className="ea-field-group ea-field-group-row">
				<VGSInput
					fieldName={VGSTypes.ExpirationDate}
					ref={expRef}
					labelFor="exp-container"
					label="Expiration Date"
					inputClass="exp-input"
				/>
				{
					/**
					 * Caging forms have no security code.
					 */
					theme.donate_type !== 'caging' && (
						<VGSInput
							fieldName={VGSTypes.SecurityCode}
							ref={cvvRef}
							labelFor="sec-container"
							label="Security Code"
							inputClass="sec-input"
						/>
					)
				}
			</div>
			<Address />
		</Fragment>
	);
};

export default Payment;
